import type { FormikErrors } from 'formik';
import React from 'react';
import { useQuery } from '@apollo/client';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import Form, { Field, FieldSet, Submit } from '../../components/Form';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { ProvisionType } from '../../graphql/generated/graphql';
import trashIcon from '../../icons/non-prefilled/16x16/trash.svg';
import type { EntitySelectConfig } from '../../search/entitySearch/CategorizedEntitySelect';
import CategorizedEntitySelect from '../../search/entitySearch/CategorizedEntitySelect';
import SettingsContainerView from '../../settings/SettingsContainerView';
import SettingsSubmitConfirmation, {
  useSubmitWithSaved,
} from '../../settings/SettingsSubmitConfirmation';
import { adminToasts, miscToasts } from '../../toasts/toasts';
import { UIActions } from '../../ui/uiSlice';
import handleApolloError from '../../utils/error/handleApolloError';
import provisionRegisterUrl from '../../utils/url/provisionRegisterUrl';
import * as styles from './AdminProvisionForm.module.scss';
import ProvisionSSODefaultBadge from './ProvisionSSODefaultBadge';
import {
  ProvisionTypeSelectOption,
  ProvisionTypeSelectSingleValue,
} from './ProvisionTypeSelectOption';
import useProvisionEntities from './useProvisionEntities';
import useProvisionEntitySearchConfig from './useProvisionEntitySearchConfig';

const PROVISION_FRAGMENT = gql(`
  fragment AdminProvisionForm_provision on Provision {
    id
    name
    type
    urlEnabled
    isSsoDefault
    favorites {
      ... on Post {
        id
      }
      ... on Topic {
        id
      }
    }
    members {
      ... on Group {
        id
      }
      ... on Post {
        id
      }
      ... on Topic {
        id
      }
    }
    ...provisionRegisterUrl_provision
  }
`);

const ADMIN_PROVISION_FORM_QUERY = gql(/** @complexity 4 */ `
  query AdminProvisionForm {
    session: currentSession {
      organization {
        id
        provisions {
          id
          name
          type
        }
      }
    }
  }
`);

type AdminProvisionFormValues = {
  favoriteIds: string[];
  memberIds: string[];
  name: string;
  type: ProvisionType;
  urlEnabled: boolean;
};

const MEMBERSHIP_CONFIG: EntitySelectConfig = {
  topic: { preferredMaxCount: 3 },
  group: { preferredMaxCount: 3 },
  post: { showIfNoOthersMatch: true },
  order: ['topic', 'group', 'post'],
};

const AdminProvisionFormFields = ({
  onRegenerateUrl,
  setFieldValue,
  url,
  values,
}: {
  onRegenerateUrl?: () => unknown;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFieldValue: (field: string, value: any) => void;
  url?: string;
  values: AdminProvisionFormValues;
}) => {
  const dispatch = useAppDispatch();
  const { favorites, memberships } = useProvisionEntities(values);
  const { urlEnabled: isUrlEnabled } = values;
  const favoriteSearchConfig = useProvisionEntitySearchConfig(values);

  const handleCopyButtonClick = async () => {
    if (url) {
      await navigator.clipboard.writeText(url);
      dispatch(UIActions.pushToast(miscToasts.shareableLinkCopied));
    }
  };

  return (
    <>
      <div className={styles.section}>
        <Field
          label="Provision name"
          name="name"
          placeholder="Engineer"
          autoFocus
          required
        />
        <Field
          label="Role"
          type="select"
          name="type"
          isClearable={false}
          options={[
            { description: 'Standard team seat', value: ProvisionType.User },
            {
              description: 'Unlocks all privileges',
              value: ProvisionType.Admin,
            },
            {
              description: 'Limited access to topics',
              value: ProvisionType.Guest,
            },
          ]}
          components={{
            Option: ProvisionTypeSelectOption,
            SingleValue: ProvisionTypeSelectSingleValue,
          }}
        />
      </div>
      <div className={styles.section}>
        <FieldSet legend="Membership">
          <CategorizedEntitySelect
            maxOptionsToShow={6}
            isClearable={false}
            onChange={(entities) => {
              setFieldValue(
                'memberIds',
                entities.map((e) => e.id),
              );
            }}
            config={MEMBERSHIP_CONFIG}
            value={memberships}
            isMulti
          />
        </FieldSet>
      </div>
      <h5>Favorites</h5>
      <div className={styles.section}>
        <CategorizedEntitySelect
          isClearable={false}
          onChange={(entities) => {
            setFieldValue(
              'favoriteIds',
              entities.map((e) => e.id),
            );
          }}
          value={favorites}
          isMulti
          {...favoriteSearchConfig}
        />
      </div>
      <h5>Registration URL</h5>
      <div className={styles.section}>
        <Field
          name="urlEnabled"
          label="Allow teammates to join with this provision using a shareable link"
          type="checkbox"
        />
        {url && isUrlEnabled && (
          <>
            <Field
              margin="compact"
              name="url"
              onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
                event.target.select();
              }}
              readOnly
              value={url}
            />
            <div className={styles.urlButtons}>
              <Button
                className="provisionUrlButton"
                variant="link"
                onClick={handleCopyButtonClick}
              >
                Copy
              </Button>
              &nbsp; · &nbsp;
              <Button variant="link" onClick={onRegenerateUrl}>
                Regenerate
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

interface AdminProvisionFormProps {
  onDelete?: (errorCallback: (e: Error) => unknown) => unknown;
  onRegenerateUrl?: () => unknown;
  onSubmit: (values: AdminProvisionFormValues) => Promise<unknown>;
  provision?: DocumentType<typeof PROVISION_FRAGMENT>;
}

const AdminProvisionForm = ({
  onDelete,
  onRegenerateUrl,
  onSubmit,
  provision,
}: AdminProvisionFormProps) => {
  const id = provision?.id;
  const url = provision && provisionRegisterUrl(provision);
  const initialValues: AdminProvisionFormValues = {
    favoriteIds: provision?.favorites.map((p) => p.id) ?? [],
    memberIds: provision?.members.map((p) => p.id) ?? [],
    name: provision?.name || '',
    type: provision?.type || ProvisionType.User,
    urlEnabled: provision?.urlEnabled ?? false,
  };
  const dispatch = useAppDispatch();

  const { isSaved, handleSubmit } = useSubmitWithSaved(onSubmit);

  const { data } = useQuery(ADMIN_PROVISION_FORM_QUERY);

  const provisions = data?.session?.organization?.provisions ?? [];

  const validate = (values: AdminProvisionFormValues) => {
    const errors: FormikErrors<AdminProvisionFormValues> = {};
    if (provisions.some((p) => p?.id !== id && p?.name === values.name)) {
      errors.name =
        'A provision with that name already exists. Please enter a unique name.';
    }

    if (
      values.type !== ProvisionType.User &&
      provisions.every((p) => p?.id !== id && p?.type !== ProvisionType.User)
    ) {
      errors.type = adminToasts.provisionValidationError.message;
    }

    return errors;
  };

  const handleDelete =
    onDelete &&
    (() =>
      onDelete((e) => {
        handleApolloError(e, 'VALIDATION', () =>
          dispatch(UIActions.pushToast(adminToasts.provisionValidationError)),
        );
      }));

  return (
    <SettingsContainerView
      parentTitle="Provisions"
      parentLink="/admin/provisions"
      title={
        <div className={styles.titleRow}>
          <h2>{initialValues.name || 'New provision'}</h2>
          {provision?.isSsoDefault && <ProvisionSSODefaultBadge />}
        </div>
      }
    >
      <Form<typeof initialValues>
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validate={validate}
      >
        {({ values, setFieldValue }) => (
          <>
            <AdminProvisionFormFields
              values={values}
              setFieldValue={setFieldValue}
              url={url}
              onRegenerateUrl={onRegenerateUrl}
            />
            <div className={styles.footer}>
              <div>
                {isSaved && <SettingsSubmitConfirmation />}
                <Submit size="large">Save</Submit>
              </div>
              {handleDelete && (
                <Button icon={trashIcon} danger onClick={handleDelete}>
                  Delete provision
                </Button>
              )}
            </div>
          </>
        )}
      </Form>
    </SettingsContainerView>
  );
};

export default AdminProvisionForm;
